import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import AdminButton from '../components/AdminButton';
import useAdminButton from '../hooks/useAdminButton';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { useTenant } from '../../../contexts/TenantContext';
import { Routes } from '../../../routes/RouteConstants';
import { formatPrice } from '../../../utils/priceUtils';
import useGetSupplies from '../../supply/useGetSupplies';
import useCloseCoupon from '../api/useCloseCoupon';
import useGetCoupons from '../api/useGetCoupons';
import useSaveCoupon from '../api/useSaveCoupon';
import CouponFormModal from '../components/CouponFormModal';
import CustomModal from '../components/modals/CustomModal';
import AdminPage from '../layout/AdminPage';

const NO_SUPPLIES = [];

const STATUS_CHIPS = {
  ACTIVE: { label: 'Aktív', color: 'success' },
  SCHEDULED: { label: 'Ütemezett', color: 'info' },
  EXPIRED: { label: 'Lejárt', color: 'default' },
  CLOSED: { label: 'Lezárva', color: 'default' },
};

const formatDiscount = (coupon) => (coupon.discountType === 'PERCENT'
  ? `${coupon.discountValue}%`
  : formatPrice(coupon.discountValue));

const formatPeriod = (coupon) =>
  `${dayjs(coupon.validFrom).format('YYYY. MM. DD.')} – ${dayjs(coupon.validTo).format('YYYY. MM. DD.')}`;

const formatSupplies = (coupon, supplies) => {
  if (coupon.isForAllSupplies) return 'Minden szolgáltatás';
  const names = coupon.supplyIds
    .map((id) => supplies.find((supply) => supply.id === id)?.name)
    .filter(Boolean);
  return names.join(', ');
};

const CouponRow = ({ coupon, supplies, onEdit, onClose }) => {
  const status = STATUS_CHIPS[coupon.status] ?? STATUS_CHIPS.EXPIRED;
  const isOver = coupon.status === 'CLOSED' || coupon.status === 'EXPIRED';

  return (
    <Paper variant="outlined" data-testid="coupon-row" sx={{ p: 2, borderRadius: 2, opacity: isOver ? 0.7 : 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Stack spacing={0.25} minWidth={0} flex={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography fontWeight={700} sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: 16 }}>
              {coupon.code}
            </Typography>
            <Chip label={status.label} color={status.color} size="small" variant="outlined" data-testid="coupon-status" />
            <Typography fontWeight={700} sx={{ color: 'text.primary' }}>−{formatDiscount(coupon)}</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {formatPeriod(coupon)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>
            {formatSupplies(coupon, supplies)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="coupon-redemptions">
            {coupon.redemptionCount} beváltás
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onEdit(coupon)}
            data-testid="coupon-edit"
            sx={{ textTransform: 'none' }}
          >
            Módosítás
          </Button>
          {coupon.status !== 'CLOSED' && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onClose(coupon)}
              data-testid="coupon-close"
              sx={{ textTransform: 'none' }}
            >
              Lezárás
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

/**
 * The tenant's discount codes. A guest types one on the confirmation page; the tenant sends it to
 * the guests who subscribed, from its own mail (DECISIONS.md D34). Every field of a coupon can be
 * changed at any time, and a coupon is never deleted, only closed: bookings made with it keep
 * pointing at it. Only for a tenant with isCouponEnabled; the menu leaves the entry out otherwise.
 */
const CouponsPage = () => {
  const { isCouponEnabled } = useTenant();
  const { filledSx } = useAdminButton();
  const { data: coupons = [], isLoading } = useGetCoupons();
  const { data: supplies } = useGetSupplies();
  const { saveCoupon, isLoading: isSaving } = useSaveCoupon();
  const { closeCoupon, isLoading: isClosing } = useCloseCoupon();
  const showSnackbar = useSnackbar();
  // undefined: the form is shut; null: a new coupon; a coupon: changing that one.
  const [couponInForm, setCouponInForm] = useState(undefined);
  const [couponToClose, setCouponToClose] = useState(null);

  if (!isCouponEnabled) return <Navigate to={Routes.DASHBOARD} replace />;

  const handleSave = (request) => saveCoupon(couponInForm?.id, request, () => {
    showSnackbar(couponInForm ? 'Kupon módosítva' : 'Kupon létrehozva', 'success');
    setCouponInForm(undefined);
  });

  const confirmClose = () => {
    closeCoupon(couponToClose.id, () => showSnackbar('Kupon lezárva', 'success'));
    setCouponToClose(null);
  };

  return (
    <AdminPage
      title="Kuponok"
      subtitle="Kedvezménykódok, amelyeket a vendégek a foglalás véglegesítésekor válthatnak be, egyszer. A kódot te küldöd ki a feliratkozóidnak, a Vendégeim oldalról letöltött listával."
    >
      <Stack spacing={2.5}>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCouponInForm(null)}
            data-testid="coupon-new"
            disableElevation
            sx={{ textTransform: 'none', ...filledSx }}
          >
            Új kupon
          </Button>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        )}

        {!isLoading && coupons.length === 0 && (
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
            Még nincs kuponod.
          </Typography>
        )}

        <Stack spacing={1.5}>
          {coupons.map((coupon) => (
            <CouponRow
              key={coupon.id}
              coupon={coupon}
              supplies={supplies ?? NO_SUPPLIES}
              onEdit={setCouponInForm}
              onClose={setCouponToClose}
            />
          ))}
        </Stack>
      </Stack>

      {couponInForm !== undefined && (
        <CouponFormModal
          coupon={couponInForm}
          supplies={supplies ?? NO_SUPPLIES}
          onClose={() => setCouponInForm(undefined)}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}

      <CustomModal open={Boolean(couponToClose)} onClose={() => setCouponToClose(null)} title="Lezárás">
        <Typography variant="body2" align="center">
          A(z) {couponToClose?.code} kupon azonnal megszűnik, többé nem lehet beváltani. Ez nem vonható
          vissza. A vele már elkészült foglalások megtartják a kedvezményes árukat.
        </Typography>
        <AdminButton text="Lezárás" onClick={confirmClose} isLoading={isClosing} />
      </CustomModal>
    </AdminPage>
  );
};

export default CouponsPage;
